import * as Phaser from "phaser"
import { AUDIO, TEX } from "../../core/keys"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyMovementSpec } from "../../data/enemies"
import type { EnemyData, GameSprite } from "../../types"
import { type FilterableGameObject, applyGlowFilter } from "../../ui/neon-fx"
import {
  type EnemyState,
  type EnemyStateContext,
  type EnemyUpdateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"
import { playSfx } from "../sfx-generator"

/**
 * Apex Commander — pattern-based shmup boss.
 *
 * OVERHAULED: Now more dynamic with reactive dodges, lane-smashing, and aggressive dive slashes.
 */
type HumanoidState =
  | "approach"
  | "patrol"
  | "snipe"
  | "prefire"
  | "recoil"
  | "sweep"
  | "retreat"
  | "return"
  | "enrage"
  | "blitz"
  | "dodge" // Reactive evasion with afterimages
  | "smash" // Phase 2: Jumps and slams a lane, creating screen-wide shake
  | "burst" // Aggressive rapid fire burst

export const HUMANOID_ANIM = {
  IDLE: "anim_boss_humanoid_idle",
  JUMP: "anim_boss_humanoid_jump",
  DASH: "anim_boss_humanoid_dash",
  CHARGE: "anim_boss_humanoid_charge",
  FIRE: "anim_boss_humanoid_fire",
} as const

export const HUMANOID_MOVEMENT = {
  action: "jetpack-human",
  approachSpeed: 1.8,
  standoffX: 620,
  bobAmplitude: 7,
  bobPeriodMs: 1100,
  trackSpeed: 0.04,
  strafeSpeed: 0.15,
  retreatSpeed: 3.2,
  retreatDurationMs: 1200,
  returnSpeed: 1.6,
} satisfies Extract<EnemyMovementSpec, { action: "jetpack-human" }>

const PATROL_AMP = 105
const PATROL_PERIOD_MS = 2600
const PATROL_SHOTS_BEFORE_SNIPE = 2 // Reduced to cycle phases faster
const SNIPE_WIND_UP_MS = 1400
const SWEEP_SPEED = 5.8
const BLITZ_Y_POSITIONS = [
  Math.round(GAME_HEIGHT * 0.18),
  Math.round(GAME_HEIGHT * 0.5),
  Math.round(GAME_HEIGHT * 0.82),
]
const BLITZ_DWELL_MS = 1100
const BLITZ_SHOOT_COOLDOWN = 220
const HADOUKEN_COLOR = 0xf97316
const HADOUKEN_CORE = 0xfff7ad
const HUMANOID_ARM_MUZZLE_OFFSET_X = 24
const HUMANOID_ARM_MUZZLE_OFFSET_Y_RATIO = 0.06

const PHASE2_HP_RATIO = 0.65
const DODGE_COOLDOWN = 3000

function clampEnemyY(y: number): number {
  return Phaser.Math.Clamp(y, 50, GAME_HEIGHT - 40)
}

export class HumanoidEnemy extends StatefulEnemyRuntime<HumanoidState> {
  private phaseTimerMs = 0
  private readonly activeHadoukenGraphics =
    new Set<Phaser.GameObjects.Graphics>()
  private lastUpdateContext: EnemyUpdateContext | null = null
  private enraged = false
  private sweepDir = 1
  private blitzIndex = 0
  private blitzDwellMs = 0
  private patrolShotCount = 0
  private sniperTargetY = GAME_HEIGHT / 2
  private damageEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null =
    null
  private fireEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null =
    null
  private glowFilter: Phaser.Filters.Glow | null = null
  private snipeGlowFilter: Phaser.Filters.Glow | null = null
  private damagedPhaseStarted = false

  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<HumanoidState>[] = [
      {
        name: "approach",
        behaviorState: "flying",
        animationState: "flying",
        onDamage: () => "retreat",
        enter: (context) => {
          context.sprite.play(HUMANOID_ANIM.DASH, true)
          context.sprite.setAlpha(0)
          context.scene.tweens.add({
            targets: context.sprite,
            alpha: 1,
            duration: 500,
            ease: "Power2",
          })
          playSfx(AUDIO.SFX_THRUSTER, 0.5)
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          const standoffX = HUMANOID_MOVEMENT.standoffX
          const homeY = enemyData.homeY ?? sprite.y
          const dt = context.delta / 16.6

          enemyData.vx = scrollCompensation - HUMANOID_MOVEMENT.approachSpeed
          const approachTargetY = clampEnemyY(homeY + Math.sin(time / 300) * 8)
          sprite.y = Phaser.Math.Linear(
            sprite.y,
            approachTargetY,
            1 - (1 - 0.1) ** (dt * 0.12),
          )

          if (sprite.x <= standoffX) {
            sprite.x = standoffX
            return "patrol"
          }
          return null
        },
      },

      {
        name: "patrol",
        behaviorState: "flying",
        animationState: "flying",
        canFire: true,
        onDamage: (ctx) => (this.tryDodge(ctx) ? "dodge" : "retreat"),
        enter: (context) => {
          this.patrolShotCount = 0
          context.sprite.play(HUMANOID_ANIM.IDLE, true)
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          const dt = context.delta / 16.6

          if (context.hpRatio < 0.3 && !this.enraged) {
            this.enraged = true
            return "enrage"
          }

          enemyData.vx = scrollCompensation

          const patrolY =
            GAME_HEIGHT / 2 +
            Math.sin((time / PATROL_PERIOD_MS) * Math.PI * 2) * PATROL_AMP
          sprite.y = Phaser.Math.Linear(
            sprite.y,
            clampEnemyY(patrolY),
            1 - (1 - 0.05) ** (dt * 1.0),
          )
          sprite.x = HUMANOID_MOVEMENT.standoffX + Math.sin(time / 680) * 5

          // Randomly trigger a Smash if in Phase 2
          if (
            this.isPhase2() &&
            Math.random() < 0.005 &&
            context.stateTime > 2000
          ) {
            return "smash"
          }

          return null
        },
      },

      {
        name: "dodge",
        behaviorState: "retreating",
        animationState: "retreating",
        enter: (ctx) => {
          this.phaseTimerMs = 400
          ctx.sprite.play(HUMANOID_ANIM.DASH, true)
          playSfx(AUDIO.SFX_WARP_JUMP, 0.6)

          // Fast vertical shift
          const shift = (Math.random() > 0.5 ? 1 : -1) * 120
          const targetY = clampEnemyY(ctx.sprite.y + shift)

          ctx.scene.tweens.add({
            targets: ctx.sprite,
            y: targetY,
            x: ctx.sprite.x + 40,
            duration: 250,
            ease: "Quint.easeOut",
          })
        },
        update: (ctx) => {
          this.phaseTimerMs -= ctx.delta
          if (ctx.time % 50 < ctx.delta) {
            this.createAfterimage(ctx)
          }
          if (this.phaseTimerMs <= 0) return "patrol"
          return null
        },
      },

      {
        name: "smash",
        behaviorState: "attacking",
        animationState: "attacking",
        enter: (ctx) => {
          this.phaseTimerMs = 1200
          ctx.sprite.play(HUMANOID_ANIM.JUMP, true)
          // Move to far right center for windup
          ctx.scene.tweens.add({
            targets: ctx.sprite,
            x: GAME_WIDTH - 100,
            y: GAME_HEIGHT / 2,
            duration: 400,
            ease: "Back.easeIn",
          })
          playSfx(AUDIO.SFX_HEAVY_CANNON, 0.5)
        },
        update: (ctx) => {
          this.phaseTimerMs -= ctx.delta
          if (
            this.phaseTimerMs < 600 &&
            ctx.sprite.x > HUMANOID_MOVEMENT.standoffX
          ) {
            // Slam forward
            ctx.sprite.play(HUMANOID_ANIM.FIRE, true)
            ctx.scene.tweens.add({
              targets: ctx.sprite,
              x: HUMANOID_MOVEMENT.standoffX - 180,
              duration: 200,
              ease: "Expo.easeOut",
              onComplete: () => {
                ctx.scene.cameras.main.shake(350, 0.045)
                ctx.vfx.flashScreen(0xffffff, 0.35, 120)
                ctx.vfx.sustainedScreenTint(0x00f0ff, 0.2, 300, 100)
                ctx.vfx.playDebrisExplosion(
                  ctx.sprite.x - 40,
                  ctx.sprite.y,
                  1.5,
                )
                playSfx(AUDIO.EXPLOSION, 0.8)
              },
            })
          }
          if (this.phaseTimerMs <= 0) return "return"
          return null
        },
      },

      {
        name: "snipe",
        behaviorState: "hovering",
        animationState: "hovering",
        canFire: false,
        onDamage: (ctx) => (this.tryDodge(ctx) ? "dodge" : "retreat"),
        enter: (context) => {
          this.phaseTimerMs = SNIPE_WIND_UP_MS
          this.sniperTargetY = clampEnemyY(context.playerY)
          context.sprite.play(HUMANOID_ANIM.CHARGE, true)
          this.snipeGlowFilter =
            context.sprite.filters?.internal.addGlow(
              0xff2200,
              1,
              0,
              1,
              false,
              5,
              8,
            ) ?? null
          playSfx(AUDIO.SFX_HADOUKEN_CHARGE, 0.85)
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          const dt = context.delta / 16.6

          enemyData.vx = scrollCompensation

          sprite.y = Phaser.Math.Linear(
            sprite.y,
            this.sniperTargetY,
            1 - (1 - 0.07) ** (dt * 0.9),
          )
          sprite.x = HUMANOID_MOVEMENT.standoffX + Math.sin(time / 200) * 3

          if (this.snipeGlowFilter) {
            const chargeProgress = context.sprite.anims.getProgress()
            this.snipeGlowFilter.outerStrength = 1 + chargeProgress * 8
            this.snipeGlowFilter.color =
              chargeProgress > 0.6 ? 0xff0000 : 0xff2200
          }

          this.phaseTimerMs -= context.delta
          if (this.phaseTimerMs <= 0) {
            return "prefire"
          }
          return null
        },
        exit: (context) => {
          if (this.snipeGlowFilter) {
            context.sprite.filters?.internal.remove(this.snipeGlowFilter)
            this.snipeGlowFilter = null
          }
        },
      },

      {
        name: "prefire",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: true,
        nextStateOnFire: "recoil",
        onDamage: () => "recoil",
        enter: (context) => {
          context.enemyData.shootCooldown = 60
          context.enemyData.humanoidHadoukenBurst = true
          context.sprite.y = this.sniperTargetY
          context.sprite.play(HUMANOID_ANIM.FIRE, true)
          Phaser.Actions.AddEffectShine(context.sprite, {
            duration: 180,
            direction: Math.PI,
            colorFactor: [2.5, 0.7, 0.1, 1],
          })
          context.vfx.sustainedScreenTint(HADOUKEN_COLOR, 0.1, 180, 80)
          playSfx(AUDIO.SFX_HADOUKEN, 1)
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          enemyData.vx = scrollCompensation
          sprite.x = HUMANOID_MOVEMENT.standoffX + Math.sin(time / 200) * 3
          sprite.y = Phaser.Math.Linear(sprite.y, this.sniperTargetY, 0.08)
          return null
        },
      },

      {
        name: "recoil",
        behaviorState: "attacking",
        animationState: "attacking",
        enter: (context) => {
          this.phaseTimerMs = 480
          context.sprite.play(HUMANOID_ANIM.DASH, true)
          context.scene.cameras.main.shake(120, 0.008)
        },
        update: (context) => {
          const { sprite, time } = context
          const dt = context.delta / 16.6

          const recoilX = HUMANOID_MOVEMENT.standoffX + 32
          sprite.x = Phaser.Math.Linear(
            sprite.x,
            recoilX,
            1 - (1 - 0.12) ** (dt * 1.2),
          )
          const bob =
            Math.sin((time / HUMANOID_MOVEMENT.bobPeriodMs) * Math.PI * 2) *
            (HUMANOID_MOVEMENT.bobAmplitude * 0.5)
          sprite.y = Phaser.Math.Linear(
            sprite.y,
            clampEnemyY((this.enemyData.homeY ?? sprite.y) + bob),
            1 - (1 - 0.08) ** (dt * 0.9),
          )

          this.phaseTimerMs -= context.delta
          if (this.phaseTimerMs <= 0) {
            if (this.enraged) return "blitz"
            return this.isPhase2() ? "sweep" : "patrol"
          }
          return null
        },
      },

      {
        name: "sweep",
        behaviorState: "tracking",
        animationState: "tracking",
        canFire: true,
        onDamage: (ctx) => (this.tryDodge(ctx) ? "dodge" : "retreat"),
        enter: (context) => {
          this.sweepDir = context.sprite.y < GAME_HEIGHT / 2 ? 1 : -1
          context.sprite.play(HUMANOID_ANIM.JUMP, true)
          playSfx(AUDIO.SFX_FLYBY, 0.4)
          // Aggressive sweep attack pattern
          context.enemyData.bossAttackPattern = "humanoid-burst"
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation } = context
          const dt = context.delta / 16.6

          enemyData.vx = scrollCompensation
          sprite.y += this.sweepDir * SWEEP_SPEED * dt
          sprite.x = Phaser.Math.Linear(
            sprite.x,
            HUMANOID_MOVEMENT.standoffX - 18,
            0.04,
          )

          if (sprite.y >= GAME_HEIGHT - 55 || sprite.y <= 55) {
            sprite.y = clampEnemyY(sprite.y)
            return "return"
          }
          return null
        },
      },

      {
        name: "retreat",
        behaviorState: "retreating",
        animationState: "retreating",
        enter: (context) => {
          this.phaseTimerMs = this.enraged
            ? Math.round(HUMANOID_MOVEMENT.retreatDurationMs * 0.65)
            : HUMANOID_MOVEMENT.retreatDurationMs
          context.sprite.play(HUMANOID_ANIM.DASH, true)
          playSfx(AUDIO.SFX_WARP_JUMP, 0.45)
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          const dt = context.delta / 16.6

          enemyData.vx = scrollCompensation
          sprite.x = Math.min(
            GAME_WIDTH - 60,
            sprite.x + HUMANOID_MOVEMENT.retreatSpeed * dt * 1.6,
          )
          const homeY = enemyData.homeY ?? sprite.y
          sprite.y = Phaser.Math.Linear(
            sprite.y,
            clampEnemyY(homeY + Math.sin(time / 120) * 18),
            1 - (1 - 0.1) ** (dt * 0.14),
          )

          this.phaseTimerMs -= context.delta
          if (this.phaseTimerMs <= 0) return "return"
          return null
        },
      },

      {
        name: "return",
        behaviorState: "tracking",
        animationState: "tracking",
        enter: (context) => {
          context.sprite.play(HUMANOID_ANIM.IDLE, true)
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          const dt = context.delta / 16.6

          enemyData.vx = scrollCompensation
          sprite.x = Phaser.Math.Linear(
            sprite.x,
            HUMANOID_MOVEMENT.standoffX,
            1 - (1 - 0.1) ** (dt * HUMANOID_MOVEMENT.returnSpeed * 0.13),
          )
          const homeY = enemyData.homeY ?? sprite.y
          sprite.y = Phaser.Math.Linear(
            sprite.y,
            clampEnemyY(
              homeY +
                Math.sin((time / HUMANOID_MOVEMENT.bobPeriodMs) * Math.PI * 2) *
                  HUMANOID_MOVEMENT.bobAmplitude,
            ),
            1 - (1 - 0.08) ** (dt * 0.1),
          )

          if (Math.abs(sprite.x - HUMANOID_MOVEMENT.standoffX) < 8) {
            return this.enraged ? "blitz" : "patrol"
          }
          return null
        },
      },

      {
        name: "enrage",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: false,
        enter: (context) => {
          this.phaseTimerMs = 800
          context.enemyData.bossAttackPattern = "rage"
          context.sprite.play(HUMANOID_ANIM.CHARGE, true)
          context.sprite.setTint(0xff2222)
          playSfx(AUDIO.SFX_ROAR, 0.7)
          Phaser.Actions.AddEffectShine(context.sprite, {
            duration: 500,
            direction: 0,
            colorFactor: [1.8, 0.4, 0.4, 1],
          })
          this.fireEmitter = context.scene.add
            .particles(context.sprite.x, context.sprite.y, TEX.FIRE, {
              speed: { min: 40, max: 110 },
              angle: { min: 230, max: 310 },
              scale: { start: 1.3, end: 0 },
              lifespan: { min: 180, max: 380 },
              tint: [0xfbbf24, 0xf97316, 0xef4444, 0xff0000],
              blendMode: "ADD",
              frequency: 28,
              quantity: 2,
            })
            .setDepth(85)
        },
        update: (context) => {
          const { sprite, time } = context
          sprite.x = HUMANOID_MOVEMENT.standoffX + Math.sin(time / 30) * 14
          sprite.y = clampEnemyY(
            (this.enemyData.homeY ?? sprite.y) + Math.sin(time / 22) * 18,
          )

          this.phaseTimerMs -= context.delta
          if (this.phaseTimerMs <= 0) {
            context.sprite.clearTint()
            return "blitz"
          }
          return null
        },
        exit: (context) => {
          context.sprite.clearTint()
        },
      },

      {
        name: "blitz",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: true,
        onDamage: (ctx) => (this.tryDodge(ctx) ? "dodge" : "retreat"),
        enter: (context) => {
          this.blitzDwellMs = BLITZ_DWELL_MS
          context.sprite.play(HUMANOID_ANIM.FIRE, true)
          if (this.enemyData.bossMaxHp !== undefined) {
            context.vfx.sustainedScreenTint(0xff2222, 0.25, 4000, 400)
            context.vfx.shakeCamera(500, 0.025)
          }
        },
        update: (context) => {
          const { sprite, enemyData, scrollCompensation, time } = context
          const dt = context.delta / 16.6

          enemyData.vx = scrollCompensation
          const targetY = BLITZ_Y_POSITIONS[this.blitzIndex]

          sprite.y = Phaser.Math.Linear(
            sprite.y,
            targetY,
            1 - (1 - 0.18) ** (dt * 0.85),
          )
          sprite.x =
            HUMANOID_MOVEMENT.standoffX - 35 + Math.sin(time / 110) * 18

          const atLane = Math.abs(sprite.y - targetY) < 22
          if (atLane) {
            this.blitzDwellMs -= context.delta
            if (enemyData.shootCooldown > BLITZ_SHOOT_COOLDOWN) {
              enemyData.shootCooldown = BLITZ_SHOOT_COOLDOWN
            }

            if (this.blitzDwellMs <= 0) {
              this.blitzIndex = (this.blitzIndex + 1) % BLITZ_Y_POSITIONS.length
              this.blitzDwellMs = BLITZ_DWELL_MS
              context.vfx.emitImpactBurst(sprite.x, sprite.y, 0xff4400, 0.55)
              context.scene.cameras.main.shake(90, 0.006)
              playSfx(AUDIO.SFX_THRUSTER, 0.35)
              const w = sprite.displayWidth
              const h = sprite.displayHeight
              context.scene.tweens.add({
                targets: sprite,
                displayWidth: { from: w * 1.22, to: w },
                displayHeight: { from: h * 1.22, to: h },
                duration: 220,
                ease: "Back.easeOut",
              })
            }
          }

          if (time % 100 < context.delta) {
            context.vfx.emitImpactBurst(sprite.x, sprite.y, 0xff0000, 0.3)
          }

          return null
        },
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  private isPhase2(): boolean {
    return (
      this.enemyData.bossMaxHp !== undefined &&
      this.enemyData.hp / this.enemyData.bossMaxHp < PHASE2_HP_RATIO
    )
  }

  private tryDodge(ctx: EnemyStateContext<HumanoidState>): boolean {
    const now = ctx.time
    const lastDodge = this.enemyData.humanoidLastDodge ?? 0
    if (now - lastDodge < DODGE_COOLDOWN) return false

    this.enemyData.humanoidLastDodge = now
    return true
  }

  private createAfterimage(ctx: EnemyStateContext<HumanoidState>): void {
    const afterimage = ctx.scene.add.sprite(
      ctx.sprite.x,
      ctx.sprite.y,
      ctx.sprite.texture.key,
      ctx.sprite.frame.name,
    )
    afterimage.setAlpha(0.6)
    afterimage.setTint(0x00f0ff)
    afterimage.setDepth(ctx.sprite.depth - 1)
    afterimage.setScale(ctx.sprite.scaleX, ctx.sprite.scaleY)

    ctx.scene.tweens.add({
      targets: afterimage,
      alpha: 0,
      scaleX: afterimage.scaleX * 0.8,
      scaleY: afterimage.scaleY * 0.8,
      duration: 300,
      onComplete: () => afterimage.destroy(),
    })
  }

  public override onFire(): void {
    if (this.lastUpdateContext) {
      this.emitArmShotEffect(this.lastUpdateContext)
    }

    if (this.getStateName() === "prefire") {
      if (this.lastUpdateContext) {
        this.emitHadoukenBurst(this.lastUpdateContext)
      }
    }

    if (this.getStateName() === "patrol") {
      this.patrolShotCount++
      if (this.patrolShotCount >= PATROL_SHOTS_BEFORE_SNIPE) {
        this.patrolShotCount = 0
        this.transitionTo("snipe")
      }
      return
    }
    super.onFire()
  }

  private getArmMuzzlePoint(): { x: number; y: number } {
    return {
      x: this.sprite.x + HUMANOID_ARM_MUZZLE_OFFSET_X,
      y:
        this.sprite.y -
        this.sprite.displayHeight * HUMANOID_ARM_MUZZLE_OFFSET_Y_RATIO,
    }
  }

  private emitArmShotEffect(context: EnemyUpdateContext): void {
    const { scene, vfx } = context
    const { x, y } = this.getArmMuzzlePoint()
    const flareColor = 0xfacc15
    const emberColor = 0xf97316
    const armFlash = scene.add.graphics()

    armFlash.setDepth(this.sprite.depth + 6)
    armFlash.setBlendMode(Phaser.BlendModes.ADD)
    applyGlowFilter(armFlash as unknown as FilterableGameObject, {
      color: flareColor,
      outerStrength: 4,
      innerStrength: 0.25,
      quality: 8,
      distance: 7,
    })

    const flashState = { progress: 0 }
    const drawArmFlash = (): void => {
      const alpha = 1 - flashState.progress
      const spread = 1 + flashState.progress * 0.8

      armFlash.clear()
      armFlash.fillStyle(0xffffff, 0.55 * alpha)
      armFlash.fillCircle(x, y, 3 * spread)
      armFlash.fillStyle(flareColor, 0.34 * alpha)
      armFlash.fillCircle(x, y, 7 * spread)
      armFlash.fillStyle(emberColor, 0.22 * alpha)
      armFlash.fillCircle(x - 5, y + 1, 6 * spread)
      armFlash.lineStyle(2.5 * spread, flareColor, 0.34 * alpha)
      armFlash.lineBetween(x + 3, y - 1, x - 13 - 5 * spread, y)
      armFlash.lineStyle(1.2 * spread, 0xffffff, 0.52 * alpha)
      armFlash.lineBetween(x + 2, y, x - 9 - 4 * spread, y)
    }

    drawArmFlash()
    scene.tweens.add({
      targets: flashState,
      progress: 1,
      duration: 120,
      ease: "Quad.easeOut",
      onUpdate: drawArmFlash,
      onComplete: () => armFlash.destroy(),
    })

    vfx.sparkEmitter.explode(3, x, y)
  }

  private emitHadoukenBurst(context: EnemyUpdateContext): void {
    const { scene, vfx } = context
    const sprite = this.sprite
    const { x: muzzleX, y: muzzleY } = this.getArmMuzzlePoint()
    const burst = scene.add.graphics()

    burst.setDepth(sprite.depth + 4)
    burst.setBlendMode(Phaser.BlendModes.ADD)
    applyGlowFilter(burst as unknown as FilterableGameObject, {
      color: HADOUKEN_COLOR,
      outerStrength: 7,
      innerStrength: 0.35,
      quality: 10,
      distance: 10,
    })
    this.activeHadoukenGraphics.add(burst)

    const pulse = { progress: 0 }
    const drawBurst = (): void => {
      const alpha = 1 - pulse.progress
      const expansion = 1 + pulse.progress * 1.3
      const offsets = [-10, 0, 10]

      burst.clear()
      for (const offsetY of offsets) {
        const travel = 18 + pulse.progress * 34
        const x = muzzleX - travel
        const y = muzzleY + offsetY * (0.5 + pulse.progress * 0.35)
        const radius = 8 + expansion * 6

        burst.fillStyle(HADOUKEN_COLOR, 0.2 * alpha)
        burst.fillCircle(x, y, radius * 1.35)
        burst.fillStyle(HADOUKEN_CORE, 0.55 * alpha)
        burst.fillCircle(x, y, radius * 0.55)
        burst.lineStyle(3.5 * expansion, HADOUKEN_COLOR, 0.42 * alpha)
        burst.strokeCircle(x, y, radius)
        burst.lineStyle(1.5 * expansion, HADOUKEN_CORE, 0.72 * alpha)
        burst.strokeCircle(x - 2, y, radius * 0.6)
      }
    }

    drawBurst()
    scene.tweens.add({
      targets: pulse,
      progress: 1,
      duration: 230,
      ease: "Expo.easeOut",
      onUpdate: drawBurst,
      onComplete: () => {
        this.activeHadoukenGraphics.delete(burst)
        burst.destroy()
      },
    })

    const muzzleRing = scene.add.circle(muzzleX, muzzleY, 14)
    muzzleRing.setDepth(sprite.depth + 5)
    muzzleRing.setBlendMode(Phaser.BlendModes.ADD)
    muzzleRing.setStrokeStyle(3, HADOUKEN_CORE, 0.9)
    applyGlowFilter(muzzleRing as unknown as FilterableGameObject, {
      color: HADOUKEN_COLOR,
      outerStrength: 6,
      innerStrength: 0,
      quality: 8,
      distance: 8,
    })
    scene.tweens.add({
      targets: muzzleRing,
      alpha: 0,
      scaleX: 1.9,
      scaleY: 1.9,
      duration: 180,
      ease: "Quad.easeOut",
      onComplete: () => muzzleRing.destroy(),
    })

    vfx.emitMuzzleFlash(muzzleX, muzzleY, Math.PI, HADOUKEN_COLOR, 0.85)
    vfx.emitImpactBurst(muzzleX - 34, muzzleY, HADOUKEN_COLOR, 0.75)
    vfx.sparkEmitter.explode(10, muzzleX, muzzleY)
    vfx.sustainedScreenTint(HADOUKEN_COLOR, 0.1, 140, 60)
    scene.cameras.main.shake(130, 0.009)
  }

  public override update(context: EnemyUpdateContext): void {
    this.lastUpdateContext = context
    super.update(context)
    this.sprite.x = Phaser.Math.Clamp(this.sprite.x, 20, GAME_WIDTH - 20)

    const hpRatio =
      this.enemyData.bossMaxHp !== undefined
        ? this.enemyData.hp / this.enemyData.bossMaxHp
        : 1
    if (
      hpRatio < 0.6 &&
      !this.damagedPhaseStarted &&
      this.enemyData.bossMaxHp !== undefined
    ) {
      this.damagedPhaseStarted = true
      this.damageEmitter = context.scene.add
        .particles(this.sprite.x, this.sprite.y, TEX.SPARK, {
          speed: { min: 15, max: 55 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.45, end: 0 },
          lifespan: { min: 180, max: 360 },
          tint: [0x00f0ff, 0xffffff, 0x66ccff],
          blendMode: "ADD",
          frequency: 85,
          quantity: 1,
        })
        .setDepth(85)
      this.glowFilter =
        this.sprite.filters?.internal.addGlow(
          0x00f0ff,
          3,
          0,
          1,
          false,
          8,
          12,
        ) ?? null
    }

    if (this.damageEmitter) {
      this.damageEmitter.setPosition(this.sprite.x, this.sprite.y)
    }
    if (this.fireEmitter) {
      this.fireEmitter.setPosition(this.sprite.x, this.sprite.y)
    }
  }

  public override destroy(): void {
    super.destroy()
    for (const graphic of this.activeHadoukenGraphics) {
      graphic.destroy()
    }
    this.activeHadoukenGraphics.clear()
    this.damageEmitter?.destroy()
    this.damageEmitter = null
    this.fireEmitter?.destroy()
    this.fireEmitter = null
    if (this.glowFilter) {
      this.sprite.filters?.internal.remove(this.glowFilter)
      this.glowFilter = null
    }
    if (this.snipeGlowFilter) {
      this.sprite.filters?.internal.remove(this.snipeGlowFilter)
      this.snipeGlowFilter = null
    }
  }
}
